export const CART_UPSELL_TAG = "cart-upsell";
export const DIGITAL_ONLY_TAG = "digital-only";

const UPSELL_FRAME_PRODUCT_TYPES = ["Custom Frame", "Curated Design Frame", "Frame Bundle"];

export function frameFitsLimits (limits, finalFrameSize) {
  if (!limits || !finalFrameSize?.width || !finalFrameSize?.height) {
    return false;
  }

  const fits = (frameWidth, frameHeight) => (
    limits.minimumFrameWidth <= frameWidth
      && limits.minimumFrameHeight <= frameHeight
      && limits.maximumFrameWidth >= frameWidth
      && limits.maximumFrameHeight >= frameHeight
  );

  return fits(finalFrameSize.width, finalFrameSize.height)
    || fits(finalFrameSize.height, finalFrameSize.width);
}

export function variantFitsFrame (variant, finalFrameSize) {
  return Boolean(variant?.purchasable) && frameFitsLimits(variant?.frameDimensionLimits, finalFrameSize);
}

export function fittingVariants (product, finalFrameSize) {
  return (product?.variants || []).filter((variant) => {
    return Boolean(variant.joinerySku) && variantFitsFrame(variant, finalFrameSize);
  });
}

export function isCartUpsellProduct (product) {
  return Boolean(product?.tags?.includes(CART_UPSELL_TAG));
}

export function upsellVariant (product) {
  const variants = product?.variants || [];

  return variants.find((variant) => variant.frameDimensionLimits) || variants[0];
}

export function variantPriceCents (variant) {
  const price = parseFloat(variant?.price);

  return Number.isNaN(price) ? 0 : Math.round(price * 100);
}

export function upsellPriceCents (product) {
  return variantPriceCents(upsellVariant(product));
}

export function frameAllowsUpsells ({ productType, isTableTop = false, isSingleFrame = true } = {}) {
  return UPSELL_FRAME_PRODUCT_TYPES.includes(productType) && !isTableTop && isSingleFrame;
}

export function eligibleUpsellProducts ({ products = [], finalFrameSize, isDigital = true, ...frame } = {}) {
  if (!frameAllowsUpsells(frame)) {
    return [];
  }

  return products.filter((product) => {
    if (!isCartUpsellProduct(product) || product.status !== "ACTIVE") {
      return false;
    }

    if (product.tags.includes(DIGITAL_ONLY_TAG) && !isDigital) {
      return false;
    }

    return variantFitsFrame(upsellVariant(product), finalFrameSize);
  });
}

export async function giftBoxEligibility (frameSpec, { isDigital = true } = {}) {
  if (!frameSpec?.product) {
    return null;
  }

  const included = Boolean(frameSpec.isAutoGiftBox());

  if (!included && (
    frameSpec.isCuratedDesignFrame()
      || !isDigital
      || !(await frameSpec.giftBoxSupportsMouldingHeight())
  )) {
    return null;
  }

  return { included };
}

export function giftBoxVariantFor (product, frameSpec, finalFrameSize) {
  if (!product || !finalFrameSize || frameSpec.isTableTopFrame()) {
    return null;
  }

  const defaultSku = frameSpec.product?.defaultGiftBoxSku;

  return (product.variants || []).find((variant) => variant.joinerySku && variant.joinerySku === defaultSku)
    || fittingVariants(product, finalFrameSize)[0]
    || null;
}

export function isChildLineOf (line, parentProperties) {
  const properties = line?.properties || {};
  const { _frame_spec_number: frameSpecNumber, _bundle_spec_number: bundleSpecNumber } = parentProperties || {};

  return Boolean(
    (frameSpecNumber && properties._parent_design === frameSpecNumber)
      || (bundleSpecNumber && (properties._parent_bundle_spec === bundleSpecNumber || properties._parent_design === bundleSpecNumber))
  );
}

export function upsellLineItems ({ products = [], parentDesignNumber, parentBundleSpecNumber } = {}) {
  const variantIdList = products.map((product) => String(upsellVariant(product).shopifyVariantId)).join(",");

  return products.map((product) => ({
    id: String(upsellVariant(product).shopifyVariantId),
    quantity: 1,
    properties: {
      _parent_design: parentDesignNumber,
      ...(parentBundleSpecNumber ? { _parent_bundle_spec: parentBundleSpecNumber } : {}),
      _upsell_item: "true",
      _upsell_item_variant_id_list: variantIdList
    }
  }));
}

export function upsellParentProperties (products = []) {
  if (!products.length) {
    return {};
  }

  const totalCents = products.reduce((sum, product) => sum + upsellPriceCents(product), 0);

  return {
    _upsell_parent: "true",
    _upsell_price: String(totalCents),
    _upsell_item_variant_id_list: products.map((product) => String(upsellVariant(product).shopifyVariantId)).join(",")
  };
}

// upsells are their own cart lines, so the frame spec price leaves them out
export function priceWithUpsells (price, products = []) {
  const upsellCents = products.reduce((sum, product) => sum + upsellPriceCents(product), 0);

  if (!upsellCents || price === null || price === undefined || Number.isNaN(Number(price))) {
    return price;
  }

  return Math.round(Number(price) * 100 + upsellCents) / 100;
}

export function upsellPriceBreakdown (products = []) {
  return products.map((product) => ({
    key: product.handle,
    presentation: product.title,
    amount: upsellPriceCents(product) / 100
  }));
}
